/**
 * Utilitaires de calcul de grades DPE / GES (A–G).
 * Utilisés par useExtractData pour produire les colonnes meta synthétiques.
 */

/** Correspondance lettre → valeur numérique */
export const DPE_GRADE_TO_NUM = { A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7 };

/** Correspondance valeur numérique → lettre */
export const DPE_NUM_TO_GRADE = { 1: 'A', 2: 'B', 3: 'C', 4: 'D', 5: 'E', 6: 'F', 7: 'G' };

/**
 * Calcule le grade médian à partir d'un champ string | string[] de lettres A–G.
 * Étape 1 : compte le nombre d'éléments par catégorie (A=1 … G=7).
 * Étape 2 : parcourt les catégories dans l'ordre pour extraire la valeur médiane.
 *   - total impair  → catégorie de l'élément central
 *   - total pair    → moyenne arrondie des deux catégories centrales
 *
 * @param {string|string[]} fieldValue
 * @returns {string|null}  Lettre A–G, ou null si aucune valeur valide
 */
export function computeMedianGrade(fieldValue) {
    const arr = Array.isArray(fieldValue)
        ? fieldValue
        : (fieldValue != null && fieldValue !== '' ? [fieldValue] : []);

    // ── Décompte par catégorie ────────────────────────────────────────────────
    const counts = {};
    let total = 0;
    for (const v of arr) {
        const n = DPE_GRADE_TO_NUM[v];
        if (n !== undefined) {
            counts[n] = (counts[n] || 0) + 1;
            total++;
        }
    }
    if (total === 0) return null;

    // ── Indices (0-based) des deux valeurs centrales ──────────────────────────
    const midLow  = Math.floor((total - 1) / 2); // même index si total impair
    const midHigh = Math.floor(total / 2);

    let cumul = 0;
    let lowVal = null, highVal = null;
    for (let g = 1; g <= 7; g++) {
        const c = counts[g] || 0;
        if (c === 0) continue;
        if (lowVal  === null && cumul + c > midLow)  lowVal  = g;
        if (highVal === null && cumul + c > midHigh) highVal = g;
        cumul += c;
        if (lowVal !== null && highVal !== null) break;
    }

    const median = Math.round((lowVal + highVal) / 2);
    return DPE_NUM_TO_GRADE[median] ?? null;
}

/**
 * Calcule le grade moyen (moyenne arithmétique arrondie) à partir d'un champ
 * string | string[] de lettres A–G.
 * Étape 1 : compte le nombre d'éléments par catégorie.
 * Étape 2 : calcule la moyenne pondérée à partir de ce décompte.
 *
 * @param {string|string[]} fieldValue
 * @returns {string|null}  Lettre A–G, ou null si aucune valeur valide
 */
export function computeMeanGrade(fieldValue) {
    const arr = Array.isArray(fieldValue)
        ? fieldValue
        : (fieldValue != null && fieldValue !== '' ? [fieldValue] : []);

    // ── Décompte par catégorie ────────────────────────────────────────────────
    const counts = {};
    let total = 0;
    for (const v of arr) {
        const n = DPE_GRADE_TO_NUM[v];
        if (n !== undefined) {
            counts[n] = (counts[n] || 0) + 1;
            total++;
        }
    }
    if (total === 0) return null;

    // ── Moyenne pondérée par catégorie ────────────────────────────────────────
    let sum = 0;
    for (const [grade, count] of Object.entries(counts)) {
        sum += Number(grade) * count;
    }
    const mean = Math.round(sum / total);
    return DPE_NUM_TO_GRADE[Math.min(7, Math.max(1, mean))] ?? null;
}

